import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  SimpleSidebarContent,
  SimpleSidebarLayout,
} from "@volca/ui/components/ui/simple-sidebar";
import type { IEnrichedBusiness } from "@/business/business";
import { resumeOnboardingPath } from "@/onboarding/routes";
import { CompanyStepScreen } from "./company-step-screen";
import { DocumentStepScreen } from "./document-step-screen";
import { ForwardingSetupScreen } from "./forwarding-setup-screen";
import { GuideContext } from "./guide-context";
import { GuidePanel } from "./guide-panel";
import type { Guide } from "./guides";
import { InvoiceSourcesScreen } from "./invoice-sources-screen";
import { InvoiceUploadScreen } from "./invoice-upload-screen";
import { NoOnboardingProducts } from "./no-onboarding-products";
import { OnboardingStateContext } from "./onboarding-state-context";
import { PreservedStep } from "./preserved-step";
import { ReviewSubmitScreen } from "./review-submit-screen";
import { ScheduleStepScreen } from "./schedule-step-screen";
import { StAccessScreen } from "./st-access-screen";
import type { StepPayload } from "./step-payload-context";
import { StepPayloadContext } from "./step-payload-context";
import { SubmittedScreen } from "./submitted-screen";
import { ONBOARDING_DOCUMENT_TYPE, type BusinessOnboarding } from "./types";
import { WelcomeScreen } from "./welcome-screen";

export const OnboardingFlow = ({
  business,
  state,
}: {
  business: IEnrichedBusiness;
  state: BusinessOnboarding;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { current_step: step, steps, answers } = state;
  const hasPayroll = steps.includes("schedule");
  const hasAp = steps.includes("invoice_sources");

  const savedPath = resumeOnboardingPath(state);
  // Held here rather than in the step: the guide slides in beside the step, so it has to
  // sit outside it in the tree.
  //
  // Stored with the step it was opened from, so moving on closes it. Leaving it open
  // carried one task's instructions beside the next one, where they are at best noise.
  // Derived rather than cleared by an effect: the guide simply belongs to its step.
  const [opened, setOpened] = useState<{ step: string; guide: Guide } | null>(
    null,
  );
  const guide = opened?.step === step ? opened.guide : null;
  const setGuide = (next: Guide | null) =>
    setOpened(next ? { step, guide: next } : null);
  // One slot, written by whichever step is on screen and read when the checklist moves.
  const stepPayload = useRef<(() => StepPayload | undefined) | null>(null);
  const guideValue = useMemo(
    () => ({ open: guide, setOpen: setGuide }),

    // render and closes over `step`, which is already a dependency through `guide`.
    [guide, step],
  );

  useEffect(() => {
    if (!steps.length) return;
    if (location.pathname !== savedPath) navigate(savedPath, { replace: true });
  }, [savedPath, steps.length, location.pathname, navigate]);

  if (!steps.length) return <NoOnboardingProducts />;
  return (
    <OnboardingStateContext.Provider value={state}>
      <StepPayloadContext.Provider value={stepPayload}>
        <GuideContext.Provider value={guideValue}>
          <SimpleSidebarLayout
            open={guide !== null}
            onOpenChange={(next) => !next && setGuide(null)}
            sidebarWidth="min(580px, 40vw)"
          >
            <SimpleSidebarContent removeScrollArea>
              <PreservedStep active={step === "welcome"}>
                <WelcomeScreen business={business} />
              </PreservedStep>
              {steps.includes("company") && (
                <PreservedStep active={step === "company"}>
                  <CompanyStepScreen business={business} />
                </PreservedStep>
              )}
              {hasPayroll && (
                <PreservedStep active={step === "schedule"}>
                  <ScheduleStepScreen business={business} />
                </PreservedStep>
              )}
              {hasPayroll && (
                <PreservedStep active={step === "reference_data"}>
                  <DocumentStepScreen
                    businessId={business.id}
                    type={ONBOARDING_DOCUMENT_TYPE.referenceData}
                  />
                </PreservedStep>
              )}
              {hasPayroll && (
                <PreservedStep active={step === "pay_plan"}>
                  <DocumentStepScreen
                    businessId={business.id}
                    type={ONBOARDING_DOCUMENT_TYPE.payPlan}
                    submittedAt={state.submitted_at}
                    isLastIntakeStep={!hasAp}
                  />
                </PreservedStep>
              )}
              {hasAp && (
                <PreservedStep active={step === "invoice_sources"}>
                  <InvoiceSourcesScreen
                    businessId={business.id}
                    savedAnswers={answers}
                  />
                </PreservedStep>
              )}
              {hasAp && (
                <PreservedStep active={step === "forwarding_setup"}>
                  <ForwardingSetupScreen
                    businessId={business.id}
                    address={state.ap_inbound_email}
                    savedAnswers={answers}
                    check={state.forwarding_check}
                  />
                </PreservedStep>
              )}
              {hasAp && (
                <PreservedStep active={step === "invoice_upload"}>
                  <InvoiceUploadScreen
                    businessId={business.id}
                    samples={state.invoice_samples}
                    address={state.ap_inbound_email}
                  />
                </PreservedStep>
              )}
              {steps.includes("st_access") && (
                <PreservedStep active={step === "st_access"}>
                  <StAccessScreen
                    businessId={business.id}
                    tenantId={business.service_titan_tenant_id ?? null}
                    stage={state.service_titan_stage}
                    savedAnswers={answers}
                  />
                </PreservedStep>
              )}
              {hasAp && step === "onboarding_call" && (
                <ReviewSubmitScreen business={business} state={state} />
              )}
              {step === "waiting" && (
                <SubmittedScreen business={business} state={state} />
              )}
            </SimpleSidebarContent>
            <GuidePanel />
          </SimpleSidebarLayout>
        </GuideContext.Provider>
      </StepPayloadContext.Provider>
    </OnboardingStateContext.Provider>
  );
};
